import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment } from "react";
import { IntlProvider } from "react-intl";
import DOMPurify from "dompurify";
import { SearchProvider, WithSearch } from "@elastic/react-search-ui";
import "@elastic/react-search-ui-views/lib/styles/styles.css";
import { i18nConfig } from "../i18nConfig";
import config from "../config";
import buildRequest from "../utils/buildRequest";
import runRequest from "../utils/runRequest";
import applyDisjunctiveFaceting from "../utils/applyDisjunctiveFaceting";
import buildState from "../utils/buildState";
import { resolveLanguage, resolveSearchIndex } from "../utils/functions";
import { UIStateStore, UIStateStoreContext } from "../stores/uiStateStore";
import { FilterOptionEnum, FilterTypeEnum, ServiceEnum } from "../models/dataModel";
import { ResultPage } from "./ResultPage";
import { SearchField } from "./search-field/SearchField";
/**
 * Main component for the Search Client. Injects the SearchProvider, resolves the initial state,
 * and based on given display value renders the app either in search field view or the results view.
 * @param root the root element
 * @param service the service where the search is embedded in
 * @param env selected environment
 * @param language the selected language
 * @param display sets the app view state (results / searchField)
 * @param applicationId the application id to be sent in the header of the API request (optional)
 * @param product selected product (optional)
 * @param version selected version (optional)
 * @param searchTerm the initial search term when app is loaded (optional)
 */
export const SearchClient = ({ root, service, display, language, product, version, searchTerm, placeholder, applicationId, env, }) => {
    const uiStore = new UIStateStore();
    const showResults = display === "results";
    // Validate service
    service = Object.keys(config.services).find((key) => key === service)
        ? service
        : ServiceEnum.TRIMBLE_HELP;
    // Set service in the UI store
    uiStore.setSelectedService(service);
    // Resolve language from given data-language & service
    language = resolveLanguage(language, service) || "en";
    // If language does not have translations, fallback to default
    const uiLanguage = i18nConfig.messages[language] ? language : i18nConfig.defaultLocale;
    // Set language in the UI store
    uiStore.setLanguage(uiLanguage);
    /**
     * If service === ServiceEnum.TRIMBLE_HELP, sets initial filters based on values in the data-product & data-version params.
     * If data-version is given, adds the filter 'Not version-specific' as well.
     */
    function resolveInitialFilters() {
        const filters = [];
        if (service === ServiceEnum.TRIMBLE_HELP) {
            const productElement = root.getAttribute("data-product");
            const versionElement = root.getAttribute("data-version");
            product = product || (productElement !== null ? DOMPurify.sanitize(productElement) : undefined) || undefined;
            version = version || (versionElement !== null ? DOMPurify.sanitize(versionElement) : undefined) || undefined;
            if (product) {
                filters.push({
                    field: FilterOptionEnum.PRODUCT,
                    values: [product],
                    type: FilterTypeEnum.ALL,
                });
                uiStore.setFacetState(FilterOptionEnum.PRODUCT, true);
            }
            if (version) {
                // Get the 'Not version-specific' translation
                const notVersionSpecific = i18nConfig.messages[uiLanguage]["facets.software_version.notVersionSpecific"];
                filters.push({
                    field: FilterOptionEnum.VERSION,
                    values: [version, notVersionSpecific],
                    type: FilterTypeEnum.ANY,
                });
                uiStore.setFacetState(FilterOptionEnum.VERSION, true);
            }
        }
        // If service is defined, add its category to the filters & open the category
        if (service) {
            filters.push({
                field: FilterOptionEnum.CATEGORY,
                values: [config.services[service].categoryName],
                type: FilterTypeEnum.ALL,
            });
            uiStore.setFacetState(FilterOptionEnum.CATEGORY, true);
            uiStore.setOpenCategory(config.services[service].categoryName);
        }
        return filters;
    }
    const searchConfig = {
        alwaysSearchOnInitialLoad: false,
        debug: false,
        hasA11yNotifications: true,
        initialState: showResults
            ? {
                filters: resolveInitialFilters(),
                searchTerm: searchTerm,
                resultsPerPage: 10,
            }
            : {
                resultsPerPage: 10,
            },
        trackUrlState: showResults,
        onResultClick: () => { },
        onAutocompleteResultClick: () => { },
        onAutocomplete: async (state) => {
            const index = resolveSearchIndex(env, language, service, uiStore.inGlobalSearchMode());
            const requestBody = await buildRequest(index, service, { searchTerm: state.searchTerm, filters: uiStore.getAutocompleteFilters() || resolveInitialFilters() }, uiStore);
            const json = await runRequest(index, requestBody, applicationId);
            let filters = (uiStore.inGlobalSearchMode() ? config.global : config.services[service]).filters;
            const openCategory = uiStore.getOpenCategory();
            if (openCategory) {
                filters = filters.concat(config.services[openCategory].filters);
            }
            const responseState = buildState(json, filters);
            return {
                autocompletedResults: responseState.results.map((result) => {
                    return { ...result, _meta: { id: result.id } };
                }),
            };
        },
        onSearch: async (state) => {
            const { resultsPerPage } = state;
            const index = resolveSearchIndex(env, language, service, uiStore.inGlobalSearchMode());
            const requestBody = await buildRequest(index, service, state, uiStore);
            // Note that this could be optimized by running all of these requests
            // at the same time. Kept simple here for clarity.
            const responseJson = await runRequest(index, requestBody, applicationId);
            if (!responseJson) {
                return;
            }
            let filters = (uiStore.inGlobalSearchMode() ? config.global : config.services[service]).filters;
            const openCategory = uiStore.getOpenCategory();
            if (openCategory) {
                filters = filters.concat(config.services[openCategory].filters);
            }
            const responseJsonWithDisjunctiveFacetCounts = await applyDisjunctiveFaceting(service, index, responseJson, state, filters, uiStore, applicationId);
            return buildState(responseJsonWithDisjunctiveFacetCounts, filters, resultsPerPage);
        },
    };
    return (_jsx(IntlProvider, { locale: language, defaultLocale: i18nConfig.defaultLocale, messages: i18nConfig.messages[language] || i18nConfig.messages[i18nConfig.defaultLocale], children: _jsx(UIStateStoreContext.Provider, { value: uiStore, children: _jsx(SearchProvider, { config: searchConfig, children: _jsx(WithSearch, { mapContextToProps: ({ addFilter, removeFilter }) => ({ addFilter, removeFilter }), children: ({ addFilter, removeFilter }) => {
                        const resultStateObserver = new MutationObserver(() => {
                            const initFilters = resolveInitialFilters();
                            initFilters.forEach((filter) => {
                                removeFilter(filter.field);
                                addFilter(filter.field, filter.values, filter.type);
                            });
                        });
                        /** Observes changes in the product/version fields */
                        resultStateObserver.observe(root, {
                            attributes: true,
                            attributeFilter: ["data-version", "data-product"],
                        });
                        return (_jsxs(Fragment, { children: [display === "results" && _jsx(ResultPage, {}), display === "searchField" && (_jsx(SearchField, { filters: resolveInitialFilters(), placeholder: placeholder }))] }));
                    } }) }) }) }));
};
